import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import {
  replaceRegionComponents,
  previewReassignment,
  ComponentOverlapError,
  type RegionComponent,
  type AreaSearchResult,
  type PreviewVenue,
} from '../../../../api/regions';

const refKey = (c: { geoType: string; geoRef: string }) => `${c.geoType}:${c.geoRef}`;

type Draft = {
  geoType: RegionComponent['geoType'];
  geoRef: string;
  label: string;
  /** Present only for geographies added this session; kept ones already have a stored polygon. */
  geojson?: unknown;
};

/**
 * Editing state for a region's composition: the working component set, add /
 * remove, the confirm-before-save preview, and the save mutation (with overlap
 * conflict surfacing). Keeps RegionBuilder render-only.
 */
export function useComposition(regionId: string, loaded: RegionComponent[] | undefined) {
  const qc = useQueryClient();
  const [comps, setComps] = useState<Draft[]>([]);
  const [affected, setAffected] = useState<PreviewVenue[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded)
      setComps(loaded.map((c) => ({ geoType: c.geoType, geoRef: c.geoRef, label: c.label })));
  }, [loaded]);

  const originalKeys = useMemo(() => new Set((loaded ?? []).map(refKey)), [loaded]);
  const added = useMemo(
    () => comps.filter((c) => !originalKeys.has(refKey(c))),
    [comps, originalKeys],
  );

  const addComp = (r: AreaSearchResult) =>
    setComps((prev) => (prev.some((c) => refKey(c) === refKey(r)) ? prev : [...prev, r]));
  const removeComp = (key: string) => setComps((prev) => prev.filter((c) => refKey(c) !== key));

  async function openConfirm() {
    setError(null);
    const moves = added.length
      ? await previewReassignment(
          regionId,
          added.map((c) => ({ geoType: c.geoType, geoRef: c.geoRef, geojson: c.geojson })),
        )
      : [];
    setAffected(moves);
  }

  async function save(onDone: () => void) {
    setSaving(true);
    setError(null);
    try {
      await replaceRegionComponents(regionId, comps);
      await qc.invalidateQueries({ queryKey: ['regions'] });
      onDone();
    } catch (e) {
      setAffected(null);
      setError(
        e instanceof ComponentOverlapError
          ? `${e.conflicts.map((c) => c.label).join(', ')} already belong to another region.`
          : 'Failed to save composition.',
      );
    } finally {
      setSaving(false);
    }
  }

  return {
    comps,
    added,
    affected,
    saving,
    error,
    addComp,
    removeComp,
    openConfirm,
    closeConfirm: () => setAffected(null),
    save,
    refKey,
  };
}
